import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { partnerGroupChannelQueryKeys } from '../api/use-partner-group-channel-query';
import { buildPartnerGroupChannelMessageMapOptions } from '../components/Partner/Community/mapPartnerGroupChannel';
import {
  appendPartnerGroupChatMessageToCache,
  applySenderIdentityFromChannelCache,
  PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
} from '../components/Partner/Community/partnerGroupChannelMessagesCache';
import {
  doesSocketChannelMatchLiveCommunityView,
  getPartnerCommunityLiveView,
} from '../components/Partner/Community/partnerCommunityLiveView';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketProvider';
import { useSocketEvent } from './useSocketEvent';
import { hydrateMediaUrls } from '../utils/mediaSas';
import {
  consumeSocketIdempotencyKey,
  getPartnerGroupChatSocketChannelId,
  mapPartnerGroupChatSocketToChatMessage,
} from '../utils/partnerGroupChatSocketPayload';

/**
 * Real-time `message:new` for Aria Handsoff (admin + partner) and partner community ChatPanel.
 * Backend emits to room user:<Users.id> for all channel members (including sender).
 * Dedupe via idempotencyKey + message id in the React Query cache.
 */
export const useMessageNewSocketListener = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const { isAdmin, isPartner, firebaseUid, partnerProfile, adminProfile } = useAuth();
  const { isConnected } = useSocket();
  const canListen = Boolean(isAdmin || isPartner);

  const messageMapOptions = useMemo(
    () =>
      buildPartnerGroupChannelMessageMapOptions({
        firebaseUid,
        partnerProfile: isAdmin ? adminProfile || partnerProfile : partnerProfile,
      }),
    [adminProfile, firebaseUid, isAdmin, partnerProfile],
  );

  const handleMessageNew = useCallback(
    (envelope) => {
      if (!canListen) return;
      if (consumeSocketIdempotencyKey(envelope?.idempotencyKey)) return;

      // Socket payloads use bare blob URLs; append cached mediaSasToken from HTTP responses.
      hydrateMediaUrls(envelope);

      const socketChannelId = getPartnerGroupChatSocketChannelId(envelope);
      const mappedMessage = mapPartnerGroupChatSocketToChatMessage(envelope, messageMapOptions);
      if (!mappedMessage?.id || !socketChannelId) {
        return;
      }

      const liveView = getPartnerCommunityLiveView();
      const isViewingSameChannel =
        doesSocketChannelMatchLiveCommunityView(socketChannelId) && Boolean(liveView.active);
      const cacheChannelId = liveView.streamChannelId || socketChannelId;
      const chatMessage = applySenderIdentityFromChannelCache(queryClient, mappedMessage, {
        limit: PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
        channelId: cacheChannelId,
      });

      if (isViewingSameChannel) {
        appendPartnerGroupChatMessageToCache(queryClient, chatMessage, {
          limit: PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
          channelId: cacheChannelId,
        });
        return;
      }

      // Not viewing this channel — refresh any open messages queries in the background.
      queryClient.invalidateQueries({
        queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
      });
    },
    [canListen, messageMapOptions, queryClient],
  );

  useSocketEvent(
    SOCKET_EVENTS.MESSAGE_NEW,
    enabled && canListen && isConnected ? handleMessageNew : null,
    [enabled, canListen, isConnected, handleMessageNew],
  );
};

export default useMessageNewSocketListener;
